import React, { useState, useEffect } from 'react';
import PageHeading from '../../components/PageHeading';
import FeatureIntro from '../../components/FeatureIntro';
import Loading from '../../components/Loading';
import {
    useMarketingAutomationsQuery,
    useMarketingSchemaQuery,
    useFormsQuery,
    usePackagesQuery,
    useBusinessInfo
} from '../../lib/Queries';
import FlowStep from '../../components/flows/FlowStep';
import useBusinessNavigate from '../../lib/useBusinessNavigate';
import MarketingAutomationTemplateModal from '../../components/MarketingAutomationTemplateModal';
import PhysicalAddressModal from '../../components/PhysicalAddressModal';
import { Link } from 'react-router-dom';
import { getBusinessUrl } from '../../lib/useBusinessNavigate';
import useBusinessPostApi from '../../lib/useBusinessPostApi';
import * as Toast from '../../lib/Toast';
import { ucallwords } from '../../lib/Utils';
import InfoMessageBlock from '~/components/InfoMessageBlock.jsx';
import SectionCategoryLabel from '~/components/SectionCategoryLabel';
import ExperimentalBadge from '~/components/ExperimentalBadge.jsx';
import Toggle from '~/components/Toggle.jsx';

const MarketingAutomationsList = ({ automations, isLoading, refetch }) => {
    if (isLoading) {
        return <Loading className="py-4" />;
    }

    if (!automations?.length) {
        return (
            <div className="panel px-6 py-4 text-gray-500">
                No marketing automations have been created yet.
            </div>
        );
    }

    return (
        <div className="panel">
            <div className="divide-y">
                {automations.map((automation, idx) => (
                    <MarketingAutomationItem
                        key={automation.id}
                        automation={automation}
                        refetch={refetch}
                    />
                ))}
            </div>
        </div>
    );
};

const MarketingAutomationItem = ({ automation, refetch }) => {
    const { data: schemaData } = useMarketingSchemaQuery(automation.flowable_type);
    const [isEnabled, setIsEnabled] = useState(automation.is_enabled);
    const navigate = useBusinessNavigate();
    const schema = schemaData?.schema || [];
    const { save: toggleEnabled, isSaving: isToggling } = useBusinessPostApi(
        `/marketing-automations/${automation.id}/toggle-enabled`
    );

    useEffect(() => {
        setIsEnabled(automation.is_enabled);
    }, [automation.is_enabled]);

    const handleToggle = async (value) => {
        if (isToggling) {
            return;
        }

        setIsEnabled(value);
        try {
            const result = await toggleEnabled({
                is_enabled: value,
            });
            if (result?.automations) {
                refetch();
                Toast.success(value ? 'Automation enabled' : 'Automation disabled');
            }
        } catch (error) {
            // Revert on error
            setIsEnabled(!value);
            console.error('Error toggling automation:', error);
        }
    };

    return (
        <div className="flex items-center">
            <FlowStep
                step={automation}
                flowableId={null} // Marketing automations apply to all objects
                flowableType={automation.flowable_type}
                schema={schema}
                triggerTypes={['condition_met']} // Only condition-based triggers
                isMarketingAutomation={true}
                onSaved={refetch}
                onClick={() => {
                    navigate(`/marketing/${automation.id}`);
                }}
                className={`flex-1`}
                isDisabled={!isEnabled}
            />

            <Toggle
                enabled={isEnabled}
                setEnabled={handleToggle}
                className={`mx-4 mb-4 ${isToggling ? 'opacity-50' : ''}`}
            />
        </div>
    );
};

const FormAutomationRow = ({ form }) => {
    const hasAutomations = form.valid_steps && form.valid_steps.length > 0;

    return (
        <div className="py-4 pr-6 flex flex-col gap-2 justify-between xs:flex-row xs:gap-0">
            <div className="flex items-center space-x-3">
                <div>
                    <div className="flex items-center space-x-2">
                        <span>{form.name || 'Unnamed Form'}</span>
                        {!hasAutomations && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                No automations
                            </span>
                        )}
                    </div>
                    {/*<div className="text-sm text-gray-500">*/}
                    {/*    {form.capabilities?.length > 0 ? ucallwords(form.capabilities.join(',')) : 'Basic Form'}*/}
                    {/*</div>*/}
                </div>
            </div>
            <Link
                to={getBusinessUrl(`/forms/${form.id}/automations`)}
                className="btn-light-pill-sm flex-none self-start uppercase"
            >
                View Automations
            </Link>
        </div>
    );
};

const PackageAutomationNote = ({ type }) => {
    const navigate = useBusinessNavigate();

    return (
        <InfoMessageBlock className="mt-4 max-w-2xl cursor-pointer" onClick={() => navigate('/packages')}>
            <strong>Note:</strong> Expiration automations are triggered directly from the package.
            Please edit the package to change these automations.
        </InfoMessageBlock>
    );
};


const SubscriptionAutomationNote = ({ type }) => {
    const navigate = useBusinessNavigate();

    return (
        <InfoMessageBlock className="mt-4 max-w-2xl cursor-pointer" onClick={() => navigate('/subscriptions')}>
            <strong>Note:</strong> Renewal, cancellation, and expiration automations are triggered directly from the subscription.
            Edit a subscription then go to the Automations tab to edit these automations.
        </InfoMessageBlock>
    );
};

const MarketingIndexPage = () => {
    const { data, isLoading, refetch } = useMarketingAutomationsQuery();
    const automations = data?.automations;
    const navigate = useBusinessNavigate();
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showAddressModal, setShowAddressModal] = useState(false);

    // Load business info to check for physical address
    const { data: businessData } = useBusinessInfo();
    const business = businessData?.business;

    // Load forms, packages, and subscriptions
    const { data: formsData, isLoading: formsLoading } = useFormsQuery();

    // Check if physical address is required and missing
    useEffect(() => {
        if (business && !business.physical_address?.trim()) {
            setShowAddressModal(true);
        }
    }, [business]);

    const forms = formsData || [];

    const handleNewAutomation = () => {
        setShowTemplateModal(true);
    };

    const handleSelectTemplate = (templateData) => {
        // Navigate to the edit page with template data as state
        navigate('/marketing/0', { state: { templateData } });
    };

    const handleAddressSuccess = () => {
        setShowAddressModal(false);
        // Optionally refetch business data or show success message
    };

    // Group forms by capabilities
    const formsByCapabilities = Object.groupBy(forms, form => {
        return forms.length > 10 ? (form.capabilities || []).join(',') : '';
    });

    return (
        <div>
            <div>
                <PageHeading
                    heading="Automations"
                    headerSibling={
                        <ExperimentalBadge className="w-fit mb-2" />
                    }
                    rightContent={
                        <button
                            type="button"
                            className="btn-pill"
                            onClick={handleNewAutomation}
                        >
                            Add Automation
                        </button>
                    }
                />

                <FeatureIntro>
                    Easily create automated marketing automations that engage clients at the right time with personalized messages to strengthen relationships and boost retention.
                </FeatureIntro>
            </div>
            <div className="mt-6 max-w-4xl">
                {/* Marketing Automations Section */}
                <div className="mb-6">
                    <SectionCategoryLabel>
                        Marketing Automations
                    </SectionCategoryLabel>
                    <MarketingAutomationsList
                        automations={automations}
                        isLoading={isLoading}
                        refetch={refetch}
                    />
                </div>

                {/* Forms Section */}
                <SectionCategoryLabel>
                    Forms
                </SectionCategoryLabel>
                <div>
                    {formsLoading && <Loading className="py-4" />}
                    {!formsLoading && forms.length === 0 && (
                        <div className="panel px-6 py-4 text-gray-500">
                            No forms have been created yet.
                        </div>
                    )}
                    {!formsLoading && Object.keys(formsByCapabilities).length > 0 &&
                        Object.keys(formsByCapabilities)
                            .sort()
                            .map(capabilities => {
                                const capabilityForms = formsByCapabilities[capabilities];
                                return (
                                    <div key={capabilities} className="mb-6">
                                        {capabilities && (
                                            <h4 className="text-xl font-medium mb-2 text-gray-700">
                                                Form - {ucallwords(capabilities)}
                                            </h4>
                                        )}
                                        <div className="panel pl-4 divide-y">
                                            {capabilityForms.map(form => (
                                                <FormAutomationRow key={form.id} form={form} />
                                            ))}
                                        </div>

                                        {capabilities.includes('package') && !capabilities.includes('subscription') && (
                                            <PackageAutomationNote />
                                        )}
                                        {capabilities.includes('subscription') && (
                                            <SubscriptionAutomationNote />
                                        )}
                                    </div>
                                );
                            })
                    }
                </div>
            </div>

            <MarketingAutomationTemplateModal
                isOpen={showTemplateModal}
                onClose={() => setShowTemplateModal(false)}
                onSelectTemplate={handleSelectTemplate}
            />

            <PhysicalAddressModal
                isOpen={showAddressModal}
                onClose={() => setShowAddressModal(false)}
                onSuccess={handleAddressSuccess}
            />
        </div>
    );
};

export default MarketingIndexPage;
